import React from 'react'
import styled, { css } from 'styled-components'
import { secondsToFullString, secondsToString } from '@mote/helpers'
import { isMobile } from '@mote/helpers/browser'
import { Slider, Tooltip, VisuallyHidden } from '@mote/ui'
import { useMotePlayer } from '../contexts/motePlayer.context'

const TRANSITION_DURATION = 150

const SLIDER_ANIMATION_VALUES = {
  initial: {
    container: {
      padding: 2
    },
    track: {
      height: 4
    },
    thumb: {
      opacity: 0,
      size: 14,
      scale: 0
    },
    tooltip: {
      opacity: 0,
      visibility: 'hidden'
    }
  },
  final: {
    container: {
      padding: 3
    },
    track: {
      height: 6
    },
    thumb: {
      opacity: 1,
      size: 14,
      scale: 1
    },
    tooltip: {
      opacity: 1,
      visibility: 'visible'
    }
  }
}

/*
 * TODO: Revisit this to fix tooltip position on drag
 */

export const ScrubberRich = ({ disabled = false, ...rest }) => {
  const {
    pause,
    play,
    playing,
    position,
    duration,
    onScrubberValueChange: handleValueChange,
    onScrubberValueChangeEnd,
    theme: {
      scrubber: theme = {
        track: { empty: {}, filled: {} },
        thumb: {},
        tooltip: {}
      }
    }
  } = useMotePlayer()

  const sliderRef = React.useRef(null)
  const tooltipRef = React.useRef(null)
  const [isActive, setIsActive] = React.useState(false)
  const [isHovering, setIsHovering] = React.useState(false)

  const screenReaderText = React.useMemo(() => {
    const played = secondsToFullString(position)
    const total = secondsToFullString(duration)

    return `Played: ${played} of total duration (${total})`
  }, [position, duration])

  const {
    container: containerStyles,
    track: trackStyles,
    thumb: thumbStyles
  } = React.useMemo(() => {
    if (isMobile()) return SLIDER_ANIMATION_VALUES.initial

    return isHovering || isActive
      ? SLIDER_ANIMATION_VALUES.final
      : SLIDER_ANIMATION_VALUES.initial
  }, [isHovering, isActive])

  const toggleTooltip = React.useCallback((styles = {}) => {
    if (!tooltipRef.current?.tip) return

    const parent = tooltipRef.current.tip.parentNode

    Object.assign(parent.style, styles)
  }, [])

  const handleOnSliderMouseMove = React.useCallback(
    event => {
      if (!event || isMobile()) return

      const tooltipEl = tooltipRef.current?.tip
      const tooltipArrowEl = tooltipEl?.nextElementSibling
      const sliderRect = sliderRef.current?.getBoundingClientRect()
      const tooltipRect = tooltipEl?.getBoundingClientRect()
      const tooltipArrowRect = tooltipArrowEl?.getBoundingClientRect()

      let percent = (100 / sliderRect.width) * (event.clientX - sliderRect.left)
      let left = event.clientX - tooltipRect.width / 2

      if (left < sliderRect.left) {
        left = sliderRect.left
      } else if (left + tooltipRect.width > sliderRect.right) {
        left = sliderRect.right - tooltipRect.width
      }

      if (percent < 0) {
        percent = 0
      } else if (percent > 100) {
        percent = 100
      }

      tooltipEl.innerHTML = secondsToString(
        Math.ceil((duration / 100) * percent)
      )
      tooltipEl.style.left = `${left}px`
      tooltipArrowEl.style.left = `${
        left + (tooltipRect.width - tooltipArrowRect.width) / 2
      }px`
    },
    [duration]
  )

  const handleValueChangeEnd = React.useCallback(
    value => {
      onScrubberValueChangeEnd(value)
      if (isActive && !isMobile()) setIsActive(false)
    },
    [onScrubberValueChangeEnd, isActive]
  )

  const handleValueChangeStart = React.useCallback(() => {
    if (disabled || isMobile()) return
    if (isHovering) setIsActive(true)
  }, [disabled, isHovering])

  const handleOnSliderMouseEnter = React.useCallback(() => {
    if (disabled || isMobile()) return
    setIsHovering(true)

    toggleTooltip(SLIDER_ANIMATION_VALUES.final.tooltip)
  }, [disabled])

  const handleOnSliderMouseLeave = React.useCallback(() => {
    if (disabled || isMobile()) return
    setIsHovering(false)

    toggleTooltip(SLIDER_ANIMATION_VALUES.initial.tooltip)
  }, [disabled])

  React.useEffect(() => {
    toggleTooltip(SLIDER_ANIMATION_VALUES.initial.tooltip)
  }, [])

  return (
    <StyledSlider
      aria-label='Audio timeline'
      size='lg'
      value={position.toFixed(2)}
      min={0}
      max={duration}
      getAriaValueText={value => secondsToFullString(value)}
      onChangeStart={handleValueChangeStart}
      onChange={handleValueChange}
      onChangeEnd={handleValueChangeEnd}
      onMouseEnter={handleOnSliderMouseEnter}
      onMouseLeave={handleOnSliderMouseLeave}
      onMouseMove={handleOnSliderMouseMove}
      focusThumbOnChange={false}
      isDisabled={disabled}
      theme={containerStyles}
      {...rest}
    >
      <StyledSliderTrack
        ref={sliderRef}
        theme={{ ...theme.track.empty, ...trackStyles }}
      >
        <StyledSliderTrackFilled
          theme={{ ...theme.track.filled, ...trackStyles }}
        />
      </StyledSliderTrack>
      <StyledTooltip
        ref={tooltip => {
          if (tooltip) tooltipRef.current = tooltip
        }}
        isOpen={true}
        content='00:00'
        direction='up'
        distance={14}
        padding={4}
        theme={theme.tooltip}
        arrow
        forceDirection
      >
        <StyledSliderThumb theme={{ ...theme.thumb, ...thumbStyles }} />
      </StyledTooltip>
      <VisuallyHidden>{screenReaderText}</VisuallyHidden>
    </StyledSlider>
  )
}

const StyledSlider = styled(Slider)`
  padding: ${({ theme }) => theme.padding}px 0 !important;
  transition: padding ${TRANSITION_DURATION}ms;

  &::before {
    content: ' ';
    position: absolute;
    top: -18px;
    bottom: -8px;
    width: 100%;
  }
`

const StyledSliderThumb = styled(Slider.Thumb)`
  transition-duration: ${TRANSITION_DURATION}ms;
  transition-property: transform, opacity, height, width, box-shadow;

  &:focus,
  &[data-focus] {
    box-shadow: 0 0 0 2px rgba(47, 166, 255, 0.6);
  }

  ${({ theme }) => css`
    background: ${theme.background};
    height: ${theme.size}px;
    width: ${theme.size}px;
    transform: translate(-${theme.size / 2}px, -50%) scale(${theme.scale});
    left: calc(-${theme.size}px / 2);

    &:active,
    &[data-active] {
      transform: translate(-${theme.size / 2}px, -50%) scale(1.15);
    }
  `}

  &:disabled,
  &[data-disabled] {
    background: #d1d1e0;
  }

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      background: #d1d1e0;
    `};
`

const StyledSliderTrack = styled(Slider.Track)`
  border-radius: 0;
  transition-property: height;
  transition-duration: ${TRANSITION_DURATION}ms;

  ${({ theme }) => css`
    background: ${theme.background};
    height: ${theme.height}px;
  `}
`

const StyledSliderTrackFilled = styled(Slider.TrackFilled)`
  width: 0%;
  border-radius: ${({ theme }) => theme.height / 2}px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  background: ${({ theme }) => theme.background};
`

const StyledTooltip = styled(Tooltip)`
  transition-property: opacity, visibility;
  transition-duration: ${TRANSITION_DURATION}ms;

  .react-tooltip-lite {
    pointer-events: none;
    user-select: none;
    text-align: center;
  }

  .react-tooltip-lite-arrow {
    pointer-events: none;
    user-select: none;
  }

  ${({ theme }) => css`
    .react-tooltip-lite {
      color: ${theme.color};
      background: ${theme.background};
    }

    .react-tooltip-lite-arrow {
      border-color: ${theme.background};
    }
  `}
`
